// The web host's own log ring — the "app log" page of the diagnostics screen,
// and the web counterpart of Android's `AppLog` and the iOS host ring.
//
// The brain keeps its own ring (`brain/runtime/logRing.ts`), which on this
// platform carries what the brain recorded THROUGH that ring: the structured
// `diag.*` events and the diagnostics centre's own notes. Everything else the
// app says goes to the browser console — `logHttpFailure` (the four lines the
// 2026-07-26 incident was diagnosed from), React's warnings, the service
// worker's messages — and that is what this ring captures. Host and brain share
// one console in a browser, so the split is by RING API rather than by package;
// the two pages together are the session.
//
// Bounded the same way the brain's ring is, by count AND by bytes, so one
// pathological line cannot evict a session's worth of context.

import { LOG_RING_SIZE, LOG_RING_MAX_BYTES } from '@merkury/brain/runtime/logRing.js';
import type { BundleLogLine } from './bundle.js';

interface ConsoleLike {
  log?: (...args: unknown[]) => void;
  info?: (...args: unknown[]) => void;
  debug?: (...args: unknown[]) => void;
  warn?: (...args: unknown[]) => void;
  error?: (...args: unknown[]) => void;
  __merkuryAppLog?: boolean;
  /** The brain's own console-capture sentinel (`runtime/logRing.ts`). */
  __merkuryCaptured?: boolean;
}

const LEVELS: [keyof ConsoleLike, string][] = [
  ['log', 'info'],
  ['info', 'info'],
  ['debug', 'debug'],
  ['warn', 'warn'],
  ['error', 'error'],
];

const ring: BundleLogLine[] = [];
const costs: number[] = [];
let bytes = 0;

/**
 * The JSON cost of a line, without serialising it: every `console.*` call in
 * the app pays this, so the accounting must not cost more than the thing it is
 * accounting for. The fixed term covers the field names, quotes and the
 * timestamp — a constant per line, not something worth measuring exactly.
 */
const LINE_OVERHEAD_BYTES = 40;

function costOf(line: BundleLogLine): number {
  return line.msg.length + line.level.length + LINE_OVERHEAD_BYTES;
}

function evict(): void {
  while (ring.length > LOG_RING_SIZE || (bytes > LOG_RING_MAX_BYTES && ring.length > 1)) {
    ring.shift();
    bytes -= costs.shift() ?? 0;
  }
  if (bytes < 0) bytes = 0;
}

/** Record a line. Exported so a host module can log without going via console. */
export function pushAppLog(level: string, msg: string, at = Date.now()): void {
  const line: BundleLogLine = { ts: at, level, msg };
  ring.push(line);
  costs.push(costOf(line));
  bytes += costs[costs.length - 1] as number;
  evict();
}

/** Oldest → newest. A copy: callers render it while the ring keeps moving. */
export function appLog(): readonly BundleLogLine[] {
  return ring.slice();
}

export function clearAppLog(): void {
  ring.length = 0;
  costs.length = 0;
  bytes = 0;
}

function fmt(v: unknown): string {
  if (typeof v === 'string') return v;
  if (v instanceof Error) return `${v.name}: ${v.message}`;
  try {
    return JSON.stringify(v) ?? String(v);
  } catch {
    return String(v);
  }
}

/**
 * Mirror `console.*` into the ring, then delegate to the original method.
 * Idempotent — a second call on the same console object is a no-op.
 *
 * Armed at module load below, because a ring that starts recording when the
 * user opens the screen has already missed the thing they opened it about. The
 * shell may call it earlier (see the report's cross-boundary note); the call is
 * idempotent either way.
 *
 * EXACTLY ONE ring may own the console. The brain's `captureConsole`
 * (`runtime/logRing.ts`) wraps it the same way, so the two share a sentinel and
 * whichever arms first keeps it: two wrappers would put every line in BOTH
 * rings, and the "brain log vs app log" split the screen shows would silently
 * become the same lines twice.
 */
export function armAppLog(target?: ConsoleLike): void {
  const console_ = target ?? (globalThis as { console?: ConsoleLike }).console;
  if (!console_ || console_.__merkuryAppLog === true || console_.__merkuryCaptured === true) return;
  for (const [method, level] of LEVELS) {
    const original = console_[method] as ((...args: unknown[]) => void) | undefined;
    if (typeof original !== 'function') continue;
    (console_ as Record<string, unknown>)[method] = (...args: unknown[]): void => {
      pushAppLog(level, args.map(fmt).join(' '));
      original.apply(console_, args);
    };
  }
  console_.__merkuryAppLog = true;
  console_.__merkuryCaptured = true;
}

// This module is in the boot import graph (the route manifest imports the
// Developer screen eagerly), so arming here means the ring covers the session
// rather than the visit.
armAppLog();
